import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Grid, Avatar, IconButton } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CandidateResume, ICandidate } from 'wisestep-api';
import { WsColors, WsTypoColors, WsTypoVariants, WsTypography } from 'wisestep-base';

import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { downloadCV, trackCV } from '../../candidateLayout/utils/cvHelper';
import { generateSubmissionBackwardUrl } from '../../submissionsNew/utils/Helper';
import { RESUME_ACTIONS } from '../types/ResumeActionTypes';
import ResumeHeaderAndActions from '../utils/ResumeHeaderAndActions';
import { fields, formatValue, getInitials, previewStyles } from '../utils/submissionPreviewHelper';

import CVViewModal from './CVViewModal';

interface ISubmissionPreview {
  submissionData: ICandidate;
  isExternalEligible: boolean;
}

const fieldRenderers: Record<
  string,
  (value: any, resumeAction?: (action: RESUME_ACTIONS, url: string) => void) => React.ReactNode
> = {
  CV: (value, resumeAction) =>
    value && resumeAction ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ResumeHeaderAndActions resume={value} resumeAction={resumeAction} isDeletable={false} />
      </Box>
    ) : (
      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK} bold>
        No CV Available
      </WsTypography>
    ),
  Name: (value) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Avatar sx={{ bgcolor: WsColors.GreyShade, color: WsColors.White }}>
        {getInitials(value)}
      </Avatar>
      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK} bold>
        {value}
      </WsTypography>
    </Box>
  ),
  'Current Location': (value) => (
    <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
      {[value?.city, value?.state, value?.country].filter(Boolean).join(', ')}
    </WsTypography>
  ),
  'Preferred Locations': (value) => (
    <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
      {Array.isArray(value) && value.length > 0
        ? value
            .map((loc) => [loc?.city, loc?.state, loc?.country].filter(Boolean).join(', '))
            .join(' | ')
        : 'N/A'}
    </WsTypography>
  ),
  Experience: (value) => (
    <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
      {Array.isArray(value?.history) && value.history.length > 0
        ? value.history.map((exp) => `${exp?.title}, ${exp?.org}`.trim()).join(' | ')
        : 'N/A'}
    </WsTypography>
  ),
  'Candidate Notes': (value) => (
    <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
      {value?.[0]?.notes || ''}
    </WsTypography>
  ),
  DOB: (value) => {
    const date = new Date(value);
    return (
      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
        {!isNaN(date.getTime()) ? date.toLocaleDateString() : 'N/A'}
      </WsTypography>
    );
  },
  'Other Documents': (value, resumeAction) =>
    value && value.length > 0 && resumeAction ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {value.map((doc, index) => (
          <ResumeHeaderAndActions
            key={index}
            resume={doc}
            resumeAction={resumeAction}
            isDeletable={false}
          />
        ))}
      </Box>
    ) : (
      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK} bold>
        No Documents Available
      </WsTypography>
    ),
  'Candidate Languages': (value) => (
    <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
      {Array.isArray(value) && value.length > 0
        ? value
            .map((lan) => [lan?.languageName, lan?.fluencyLevel].filter(Boolean).join('->'))
            .join(' | ')
        : 'N/A'}
    </WsTypography>
  ),
  'Available From': (value) => {
    const date = new Date(value);
    return (
      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
        {!isNaN(date.getTime()) ? date.toLocaleDateString() : 'N/A'}
      </WsTypography>
    );
  },
  'Skill Matrix Ratings': (value) => (
    <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
      {Array.isArray(value) && value.length > 0
        ? value.map((skill) => [skill?.skill, skill?.rating].filter(Boolean).join('->')).join(' | ')
        : 'N/A'}
    </WsTypography>
  ),
};

const SubmissionPreview = ({ submissionData, isExternalEligible = false }: ISubmissionPreview) => {
  const [openCvView, setOpenCvView] = useState(false);
  const [resumeURL, setResumeURL] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const submittedResume: CandidateResume = {
    resumeFileName:
      submissionData?.resume?.resumeFileOriginalName ||
      submissionData?.resumeFileOriginalName ||
      '',
    resumeLocation: submissionData?.resume?.resumeLocation || submissionData?.resumeLocation || '',
  };

  const submittedDocuments =
    submissionData?.otherDocuments?.map((doc) => ({
      resumeFileName: doc?.originalName || '',
      resumeLocation: doc?.documentLocation || '',
    })) || [];

  const resumeAction = (action: RESUME_ACTIONS, url = '', docName?: string) => {
    if (!url) return;
    if (action === RESUME_ACTIONS.VIEW) {
      trackCV('view', url);
      setResumeURL(url);
      setOpenCvView(true);
    } else if (action === RESUME_ACTIONS.DOWNLOAD) {
      trackCV('download', url);
      downloadCV(url, docName);
    }
  };
  const handleEdit = () => {
    navigate(
      `/candidate-layout/submissions?id=${submissionData?.candidateId}&array=${[
        submissionData?.candidateId,
      ]}&backwardpathArray=${getEncodedQueryParams(generateSubmissionBackwardUrl())}`,
      { state: { from: location.pathname, submittedJobId: submissionData?.jobId } }
    );
  };

  return (
    <>
      {!isExternalEligible && (
        <Box sx={previewStyles.warningBox}>
          <WarningAmberIcon sx={previewStyles.warningIcon} />
          <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
            You are not allowed to submit externally
          </WsTypography>
        </Box>
      )}

      <Grid item xs={12} sx={previewStyles.sectionHeader}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK} bold>
          Review & Proceed
        </WsTypography>
        {!isEmpty(submissionData?.id) && (
          <IconButton aria-label="edit" onClick={handleEdit}>
            <EditIcon color={WsColors.IconBackGround} />
          </IconButton>
        )}
      </Grid>

      <Grid container sx={previewStyles.mainGridContainer}>
        {fields
          .filter(({ key }) => {
            const value = submissionData[key];
            return (
              typeof value === 'boolean' ||
              (typeof value === 'number' && value !== 0) ||
              !isEmpty(value)
            );
          })
          .map(({ label, key }, index, arr) => (
            <Grid container key={key} sx={previewStyles.rowContainer(index === arr.length - 1)}>
              <Grid item xs={6} sx={previewStyles.labelColumn}>
                <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                  {label}
                </WsTypography>
              </Grid>

              <Grid item xs={6} sx={previewStyles.valueColumn}>
                {fieldRenderers[label] ? (
                  fieldRenderers[label](
                    label === 'CV'
                      ? submittedResume
                      : label === 'Other Documents'
                      ? submittedDocuments
                      : submissionData[key],
                    resumeAction
                  )
                ) : (
                  <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                    {formatValue(submissionData[key])}
                  </WsTypography>
                )}
              </Grid>
            </Grid>
          ))}
      </Grid>
      {!isEmpty(submittedResume.resumeLocation) && (
        <CVViewModal open={openCvView} s3url={resumeURL} handleClose={() => setOpenCvView(false)} />
      )}
    </>
  );
};

export default SubmissionPreview;
